/**
 * ScopeInclusionsPanel — transparency disclosure for the indicative ballpark.
 *
 * Renders directly under the headline £21–28k range on the IndicativeTool
 * output screen. Two columns: what's included (green ✓) and what's not (grey ✗).
 * Optional "Want this added?" upsell line at the bottom that lets the user
 * jump straight back to Screen 1 with a different scope preselected.
 *
 * The data is sourced from the backend `/api/indicative/calculate` response
 * (`inclusions` field) — single source of truth keyed off ASSUMPTION_LIBRARY
 * so the copy never drifts from the engine assumptions.
 */
import React from 'react';
import { Check, X, ArrowRight } from 'lucide-react';

const C = {
  border: '#E2E8F0',
  navy: '#0F2742',
  text: '#334155',
  muted: '#64748B',
  green: '#16A34A',
  greenBg: '#F0FDF4',
  greenBorder: '#BBF7D0',
  greyBg: '#F8FAFC',
  greyBorder: '#E2E8F0',
  blue: '#2563EB',
  blueBg: '#EFF6FF',
  blueBorder: '#BFDBFE',
};

export const ScopeInclusionsPanel = ({ inclusions, onUpsell }) => {
  if (!inclusions || (!inclusions.included?.length && !inclusions.excluded?.length)) {
    return null;
  }

  const { label, included = [], excluded = [], upsell } = inclusions;

  return (
    <div data-testid="scope-inclusions-panel" style={{
      background: '#fff',
      border: `1.5px solid ${C.border}`,
      borderRadius: 12,
      padding: '20px 22px',
      marginBottom: 14,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: C.muted,
        textTransform: 'uppercase', marginBottom: 4,
      }}>
        What's included in this estimate
      </div>
      {label && (
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 14 }}>
          {label}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 14,
      }}>
        {/* Included column */}
        <div data-testid="inclusions-included" style={{
          background: C.greenBg,
          border: `1px solid ${C.greenBorder}`,
          borderRadius: 10,
          padding: '14px 16px',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#15803D',
            textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10,
          }}>
            ✓ Included
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {included.map((item, idx) => (
              <li key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                fontSize: 12.5, lineHeight: 1.45, color: C.text,
              }}>
                <Check size={14} strokeWidth={2.5} style={{ color: C.green, flexShrink: 0, marginTop: 2 }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Excluded column */}
        <div data-testid="inclusions-excluded" style={{
          background: C.greyBg,
          border: `1px solid ${C.greyBorder}`,
          borderRadius: 10,
          padding: '14px 16px',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: C.muted,
            textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10,
          }}>
            ✗ Not included
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {excluded.map((item, idx) => (
              <li key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                fontSize: 12.5, lineHeight: 1.45, color: C.muted,
              }}>
                <X size={14} strokeWidth={2.5} style={{ color: '#94A3B8', flexShrink: 0, marginTop: 2 }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Upsell hint */}
      {upsell && upsell.scope_id && (
        <button
          type="button"
          data-testid="inclusions-upsell-btn"
          onClick={() => onUpsell && onUpsell(upsell.scope_id)}
          style={{
            marginTop: 14,
            width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12,
            background: C.blueBg,
            border: `1px solid ${C.blueBorder}`,
            borderRadius: 10,
            padding: '12px 16px',
            cursor: onUpsell ? 'pointer' : 'default',
            textAlign: 'left',
            transition: 'background-color .15s ease',
          }}
          onMouseEnter={(e) => { if (onUpsell) e.currentTarget.style.backgroundColor = '#DBEAFE'; }}
          onMouseLeave={(e) => { if (onUpsell) e.currentTarget.style.backgroundColor = C.blueBg; }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>
              {upsell.label}
            </div>
            <div style={{ fontSize: 12, color: C.text }}>
              {upsell.delta_label}
            </div>
          </div>
          {onUpsell && <ArrowRight size={16} style={{ color: C.blue, flexShrink: 0 }} />}
        </button>
      )}
    </div>
  );
};

export default ScopeInclusionsPanel;
